import React, { useEffect, useState, useRef, useMemo } from "react";
import {
  Dialog,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import { DraggableDialogContent } from "@/components/ui/draggable-dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { GripVertical, X, Search, RotateCcw } from "lucide-react";
import { trackPromise } from "react-promise-tracker";
import { GetManageColumns, SaveManageColumns } from "redux/actions";
import { useDispatch, useSelector } from "react-redux";
import GridDataFormater from "utils/GridDataFormater";

const reorder = (list, startIndex, endIndex) => {
  const result = Array.from(list);
  const [removed] = result.splice(startIndex, 1);
  result.splice(endIndex, 0, removed);
  return result;
};

const ManageColumnsPicker = ({
  onHide,
  columns,
  show,
  isTableView,
  orginalColumns,
  clientId,
  entityName,
  setColumnDefs,
  disabledFields,
}) => {
  const dispatch = useDispatch();
  const { manage_columns } = useSelector((state) => state.configuration);
  const [areColumnRestored, setAreColumnRestored] = useState(false);
  const [temporaryColumns, setTemporaryColumns] = useState([]);
  const [disabelSave, setDisabelSave] = useState(true);
  const [search, setSearch] = useState("");
  const [recentlyAdded, setRecentlyAdded] = useState(null);
  // Tracked in state (not refs) so we can render a drop-line indicator on the
  // hovered row while dragging.
  const [dragState, setDragState] = useState({ from: null, over: null });
  const selectedScrollRef = useRef(null);
  const [columnOrder, setColumnOrder] = useState(
    columns?.filter((c) => c.headerName)
  );
  const [selectedColumns, setSelectedColumns] = useState(
    columns?.filter(
      (c) => c.headerName && (!c.hide || (c.field === "playName" && isTableView))
    )
  );

  // Required = pinned-left, the special playName column in TableView, or in
  // the disabledFields prop. These show a "Required" badge and can't be
  // unchecked or removed.
  const isRequired = (column) =>
    column.pinned === "left" ||
    (column.field === "playName" && isTableView) ||
    disabledFields?.includes(column.headerName) ||
    disabledFields?.includes(column.field);

  const onColumnUpdate = (cols) => {
    const dataColumns = cols.filter((c) => c.headerName && c.field);
    let dataColumnIndex = 0;
    cols.forEach((c) => {
      if (c.headerName && c.field) {
        c.order = dataColumnIndex;
        c.customOrder = dataColumnIndex;
        dataColumnIndex++;
      }
      if (
        c.headerName?.toLowerCase().includes("date") ||
        c.field?.toLowerCase().includes("date")
      ) {
        c.valueFormatter = GridDataFormater.vNextformatDateTimefromUTC;
        c.comparator = dateComparator;
      }
    });
    setColumnDefs(cols);
    onHide();
    dispatch(SaveManageColumns(clientId, cols, entityName));
  };

  useEffect(() => {
    if (clientId > 0) {
      trackPromise(
        dispatch(GetManageColumns(clientId, entityName)).then(() => {})
      );
    }
  }, [clientId]);

  const dateComparator = (date1, date2) => {
    var date1Number = _dateToNum(date1);
    var date2Number = _dateToNum(date2);
    if (date1Number === null && date2Number === null) return 0;
    if (date1Number === null) return -1;
    if (date2Number === null) return 1;
    return date1Number - date2Number;
  };

  const _dateToNum = (date) => {
    if (date === undefined || date === null) return null;
    const parsedDate = new Date(date);
    if (isNaN(parsedDate)) {
      console.error("Invalid date format:", date);
      return null;
    }
    const yearNumber = parsedDate.getFullYear();
    const monthNumber = String(parsedDate.getMonth() + 1).padStart(2, "0");
    const dayNumber = String(parsedDate.getDate()).padStart(2, "0");
    return parseInt(`${yearNumber}${monthNumber}${dayNumber}`, 10);
  };

  useEffect(() => {
    if (!show) return;
    setColumnOrder(columns?.filter((c) => c.headerName));
    setSelectedColumns(
      columns?.filter(
        (c) =>
          c.headerName && (!c.hide || (c.field === "playName" && isTableView))
      )
    );
    setSearch("");
    setRecentlyAdded(null);
  }, [show, columns]);

  useEffect(() => {
    if (orginalColumns) {
      if (areColumnRestored) {
        setTemporaryColumns(
          Array.isArray(orginalColumns[0]) ? orginalColumns[0] : orginalColumns
        );
        setColumnOrder(orginalColumns?.filter((c) => c.headerName));
        setSelectedColumns(
          orginalColumns?.filter(
            (c) =>
              c.headerName &&
              (!c.hide || (c.field === "playName" && isTableView))
          )
        );
      }
      if (!areColumnRestored && temporaryColumns?.length > 0) {
        setTemporaryColumns([]);
        setColumnOrder(columns?.filter((c) => c.headerName));
        setSelectedColumns(
          columns?.filter(
            (c) =>
              c.headerName &&
              (!c.hide || (c.field === "playName" && isTableView))
          )
        );
        setDisabelSave(true);
      }
    }
  }, [areColumnRestored]);

  const handleOrderChange = () => {
    let _menuColumn = columns?.filter((c) => !c.headerName);
    let _columnOrder = [];
    selectedColumns.forEach((s) => _columnOrder.push(s));
    let _remainingColumns = columnOrder.filter(
      (x) => !_columnOrder.map((y) => y.field).includes(x.field)
    );
    let _updatedColumns =
      _menuColumn.length > 0
        ? _menuColumn.concat(_columnOrder).concat(_remainingColumns)
        : _columnOrder.concat(_remainingColumns);
    _updatedColumns.forEach((u) => {
      if (u.hide === undefined) u.hide = false;
    });
    onColumnUpdate(_updatedColumns);
    setAreColumnRestored(false);
  };

  const handleSelectedColumns = (event, column) => {
    const checked = event.target.checked;
    setColumnOrder((prev) =>
      prev.map((c) =>
        c.headerName === column.headerName ? Object.assign(c, { hide: !checked }) : c
      )
    );
    if (!checked) {
      setSelectedColumns((prev) => prev.filter((s) => s.field !== column.field));
    } else {
      setSelectedColumns((prev) => [...prev, column]);
      setRecentlyAdded(column.field);
      // Scroll the Selected pane to the bottom so the newly-added (and
      // highlighted) row is visible without manual scrolling.
      window.requestAnimationFrame(() => {
        const node = selectedScrollRef.current;
        if (node) node.scrollTop = node.scrollHeight;
      });
      // Clear the highlight after the pulse animation finishes so it doesn't
      // linger if the user keeps interacting.
      window.setTimeout(() => {
        setRecentlyAdded((curr) => (curr === column.field ? null : curr));
      }, 1500);
    }
    setDisabelSave(false);
  };

  const handleRemoveSelectedColumn = (column) => {
    setColumnOrder((prev) =>
      prev.map((c) =>
        c.headerName === column.headerName ? Object.assign(c, { hide: true }) : c
      )
    );
    setSelectedColumns((prev) => prev.filter((s) => s.field !== column.field));
    setDisabelSave(false);
  };

  const dragIndexRef = useRef(null);
  const dragOverIndexRef = useRef(null);

  const handleDragStart = (index) => {
    dragIndexRef.current = index;
    setDragState({ from: index, over: null });
  };

  const handleDragEnter = (index) => {
    dragOverIndexRef.current = index;
    setDragState((s) => ({ ...s, over: index }));
  };

  const clearDragState = () => {
    dragIndexRef.current = null;
    dragOverIndexRef.current = null;
    setDragState({ from: null, over: null });
  };

  const handleDrop = () => {
    if (dragIndexRef.current === null || dragOverIndexRef.current === null) {
      clearDragState();
      return;
    }
    if (dragIndexRef.current === dragOverIndexRef.current) {
      clearDragState();
      return;
    }
    const _selectedColumns = reorder(
      selectedColumns,
      dragIndexRef.current,
      dragOverIndexRef.current
    );
    setSelectedColumns(_selectedColumns);
    setDisabelSave(false);
    clearDragState();
  };

  // Available list — alphabetical, optionally filtered by search.
  const filteredAvailable = useMemo(() => {
    const sorted = [...(columnOrder || [])].sort((a, b) =>
      (a.headerName || "").localeCompare(b.headerName || "")
    );
    if (!search.trim()) return sorted;
    const needle = search.trim().toLowerCase();
    return sorted.filter((c) =>
      (c.headerName || "").toLowerCase().includes(needle)
    );
  }, [columnOrder, search]);

  const handleCancel = () => {
    trackPromise(
      dispatch(GetManageColumns(clientId, entityName)).then(() => {})
    );
    setAreColumnRestored(false);
    setDisabelSave(true);
    onHide();
  };

  return (
    <Dialog
      open={show}
      onOpenChange={(open) => {
        if (!open) handleCancel();
      }}
    >
      <DraggableDialogContent size="lg" className="manage-column-picker">
        <DialogHeader>
          <DialogTitle className="tw:text-lg tw:font-semibold tw:text-gray-900">
            Manage columns
          </DialogTitle>
          <DialogDescription className="tw:text-sm tw:text-gray-500">
            Choose which columns are visible and drag to reorder.
          </DialogDescription>
        </DialogHeader>

        <div className="tw:grid tw:grid-cols-2 tw:gap-6 tw:py-4">
          {/* Available */}
          <div className="tw:flex tw:flex-col tw:min-h-0">
            <div className="tw:text-[11px] tw:font-bold tw:uppercase tw:tracking-wider tw:text-gray-500 tw:mb-2">
              Available
            </div>
            <div className="tw:relative tw:mb-3">
              <Search
                aria-hidden="true"
                className="tw:w-4 tw:h-4 tw:absolute tw:left-2.5 tw:top-1/2 tw:-translate-y-1/2 tw:text-gray-400 tw:pointer-events-none"
              />
              <Input
                type="text"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search columns..."
                className="tw:pl-8"
              />
            </div>
            <div className="tw:overflow-y-auto tw:h-[420px] tw:pr-2 tw:-mr-2">
              {filteredAvailable.map((column) => {
                const required = isRequired(column);
                const checked =
                  !column.hide ||
                  (column.field === "playName" && isTableView);
                return (
                  <label
                    key={column.field || column.headerName}
                    className={`tw:flex tw:items-center tw:gap-2.5 tw:py-2 tw:px-2 tw:rounded tw:transition tw:hover:bg-gray-50 ${
                      required ? "tw:cursor-default" : "tw:cursor-pointer"
                    }`}
                  >
                    <input
                      type="checkbox"
                      checked={checked}
                      disabled={required}
                      className="tw:w-4 tw:h-4 tw:cursor-pointer disabled:tw:cursor-not-allowed tw:accent-brand-secondary"
                      onChange={(e) => handleSelectedColumns(e, column)}
                    />
                    <span className="tw:flex-1 tw:text-sm tw:text-gray-900">
                      {column.headerName}
                    </span>
                    {required && (
                      <Badge
                        variant="secondary"
                        className="tw:text-[10px] tw:px-1.5 tw:py-0"
                      >
                        Required
                      </Badge>
                    )}
                  </label>
                );
              })}
              {filteredAvailable.length === 0 && (
                <div className="tw:text-sm tw:text-gray-400 tw:text-center tw:py-6">
                  No columns match "{search}"
                </div>
              )}
            </div>
          </div>

          {/* Selected */}
          <div className="tw:flex tw:flex-col tw:min-h-0 tw:border-l tw:border-gray-200 tw:pl-6">
            <div className="tw:text-[11px] tw:font-bold tw:uppercase tw:tracking-wider tw:text-gray-500 tw:mb-2">
              Selected ({selectedColumns?.length || 0})
            </div>
            <div
              ref={selectedScrollRef}
              onDragOver={(e) => e.preventDefault()}
              onDrop={handleDrop}
              className="tw:overflow-y-auto tw:h-[460px] tw:pr-2 tw:-mr-2"
            >
              {selectedColumns.map((column, i) => {
                const required = isRequired(column);
                const isHighlight = recentlyAdded === column.field;
                const isDragging = dragState.from !== null;
                const isDragSource = dragState.from === i;
                const showDropLine =
                  isDragging &&
                  dragState.over === i &&
                  dragState.from !== i;
                return (
                  <div
                    key={column.field}
                    draggable={!required}
                    onDragStart={() => handleDragStart(i)}
                    onDragEnter={() => handleDragEnter(i)}
                    onDragOver={(e) => e.preventDefault()}
                    onDrop={handleDrop}
                    onDragEnd={clearDragState}
                    className={`tw:flex tw:items-center tw:gap-2 tw:py-2 tw:px-2 tw:mb-1.5 tw:rounded tw:border tw:transition ${
                      required
                        ? "tw:bg-gray-50 tw:border-gray-200"
                        : "tw:bg-gray-50 tw:border-gray-200 tw:cursor-grab tw:hover:bg-gray-100 tw:active:cursor-grabbing"
                    } ${
                      isHighlight
                        ? "tw:!bg-brand-secondary/10 tw:!border-brand-secondary/40"
                        : ""
                    } ${isDragSource ? "tw:opacity-40" : ""} ${
                      showDropLine
                        ? "tw:!border-t-2 tw:!border-t-brand-secondary"
                        : ""
                    }`}
                    title={
                      required
                        ? "Required column — can't remove"
                        : "Drag to reorder"
                    }
                  >
                    <GripVertical
                      aria-hidden="true"
                      className={`tw:w-4 tw:h-4 tw:shrink-0 ${
                        required ? "tw:text-gray-300" : "tw:text-gray-400"
                      }`}
                    />
                    <span className="tw:flex-1 tw:text-sm tw:text-gray-900 tw:truncate">
                      {column.headerName}
                    </span>
                    {required ? (
                      <Badge
                        variant="secondary"
                        className="tw:text-[10px] tw:px-1.5 tw:py-0"
                      >
                        Required
                      </Badge>
                    ) : (
                      <button
                        type="button"
                        onClick={() => handleRemoveSelectedColumn(column)}
                        className="tw:p-1 tw:rounded tw:bg-transparent tw:border-0 tw:text-gray-400 tw:hover:text-gray-700 tw:hover:bg-gray-200 tw:cursor-pointer tw:transition"
                        aria-label={`Remove ${column.headerName}`}
                        title="Remove from view"
                      >
                        <X aria-hidden="true" className="tw:w-3.5 tw:h-3.5" />
                      </button>
                    )}
                  </div>
                );
              })}
              {selectedColumns.length === 0 && (
                <div className="tw:text-sm tw:text-gray-400 tw:text-center tw:py-6">
                  No columns selected.
                </div>
              )}
            </div>
          </div>
        </div>

        <DialogFooter className="tw:!flex tw:!items-center tw:!justify-between tw:!gap-2">
          <Button
            variant="ghost"
            onClick={() => {
              setAreColumnRestored(true);
              setDisabelSave(false);
            }}
            className="tw:!min-w-0 tw:text-gray-600 tw:hover:text-gray-900"
          >
            <RotateCcw aria-hidden="true" className="tw:w-4 tw:h-4 tw:mr-1.5" />
            Restore default
          </Button>
          <div className="tw:flex tw:gap-2">
            <Button variant="outline" onClick={handleCancel}>
              Cancel
            </Button>
            <Button
              variant="secondary"
              disabled={disabelSave}
              onClick={handleOrderChange}
            >
              Save view
            </Button>
          </div>
        </DialogFooter>
      </DraggableDialogContent>
    </Dialog>
  );
};

export default ManageColumnsPicker;
